import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useInputActionHandler } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type {
    CopilotChatLayoutProps,
    CopilotFloatingBounds
} from 'app/modules/wallforming/components/copilot/types/copilotTypes';

export const useCopilotLayout = () => {
    const selectedSettingView = usePlacementStore((state) => state.selectedSettingView);
    const setSelectedSettingView = usePlacementStore((state) => state.setSelectedSettingView);
    const previousSettingViewRef = useRef<SettingView>(
        selectedSettingView && selectedSettingView !== SettingView.COPILOT
            ? selectedSettingView
            : SettingView.AUTOMATED_PLACEMENT
    );

    const [isFloating, setIsFloating] = useState<boolean>(false);
    const [floatingBounds, setFloatingBounds] = useState<CopilotFloatingBounds>(() => ({
        x: Math.max(12, window.innerWidth - 480 - 12),
        y: 12,
        width: 480,
        height: 640
    }));
    const isOpen = selectedSettingView === SettingView.COPILOT || isFloating;

    useEffect(
        () =>
            usePlacementStore.subscribe(
                (state) => state.selectedSettingView,
                (view) => {
                    if (view && view !== SettingView.COPILOT) {
                        previousSettingViewRef.current = view;
                    }
                }
            ),
        []
    );

    const onClose = useCallback(() => {
        setIsFloating(false);
        if (usePlacementStore.getState().selectedSettingView === SettingView.COPILOT) {
            setSelectedSettingView(previousSettingViewRef.current);
        }
    }, [setSelectedSettingView]);

    const selectSettingView = useCallback(
        (view: SettingView) => {
            if (view === SettingView.COPILOT) {
                if (isFloating) {
                    return;
                }
                const currentView = usePlacementStore.getState().selectedSettingView;
                if (currentView && currentView !== SettingView.COPILOT) {
                    previousSettingViewRef.current = currentView;
                }
            }
            setSelectedSettingView(view);
        },
        [isFloating, setSelectedSettingView]
    );

    useInputActionHandler(InputActionId.GeneralToggleCopilot, () => {
        if (isOpen) {
            onClose();
        } else {
            selectSettingView(SettingView.COPILOT);
        }
    });

    const onPopOut = useCallback(() => {
        setIsFloating(true);
        setSelectedSettingView(previousSettingViewRef.current);
    }, [setSelectedSettingView]);

    const onDock = useCallback(() => {
        setIsFloating(false);
        setSelectedSettingView(SettingView.COPILOT);
    }, [setSelectedSettingView]);

    const layout = useMemo<CopilotChatLayoutProps>(
        () => ({
            isOpen,
            isFloating,
            floatingBounds,
            onClose,
            onPopOut,
            onDock,
            onFloatingBoundsChange: setFloatingBounds
        }),
        [isOpen, isFloating, floatingBounds, onClose, onPopOut, onDock]
    );

    return { layout, selectSettingView };
};
